<template>
  <div v-if="modelImportModuleInitialized">
    <v-stepper
      v-model="stepper"
      class="bg-transparent"
      flat
      height="100%"
      non-linear
    >
      <v-stepper-header
        class="elevation-0 radius-0 ant-border-bottom px-5 background-white"
      >
        <v-stepper-step
          editable
          step="1"
          @click="$store.commit('module_model_import_set_model', null)"
        >
          Select Model
        </v-stepper-step>

        <v-divider />

        <v-stepper-step step="2">
          Import Module/Elements/Objects and SBS
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items style="height: calc(100% - 72px)">
        <v-stepper-content class="pa-0 full-height" step="1">
          <div class="d-flex flex-column full-height">
            <div class="flex-1">
              <div v-if="thumbnailsLoaded" class="model-tiles-container pa-2">
                <div
                  v-if="
                    !modelImportModuleModels ||
                    modelImportModuleModels.length === 0
                  "
                  class="empty-state-message"
                >
                  <p class="text-center pa-2">
                    No models connected to ANT yet. You can add models using the
                    menu below.
                  </p>
                </div>
                <div
                  v-for="model in modelImportModuleModels"
                  v-else
                  :key="model.urn"
                  class="model-tile ant-glass-background d-flex flex-column pos-rel"
                  @click="selectModel(model)"
                >
                  <div class="pa-2">
                    {{ model.name }}
                  </div>
                  <v-img :src="model.thumbnail" alt="" height="100"></v-img>
                  <v-chip
                    color="primary"
                    small
                    style="position: absolute; right: 10px; bottom: 7px"
                    >V{{ model.version }}
                  </v-chip>
                  <v-chip
                    small
                    :color="combinedByUrn(model.urn).color"
                    style="position: absolute; left: 10px; bottom: 7px"
                  >
                    {{ combinedByUrn(model.urn).label }}
                  </v-chip>
                </div>
              </div>
              <div
                v-else
                class="d-flex justify-center align-center full-height"
              >
                <ant-loading />
              </div>
            </div>
            <div
              v-if="isAccConnected"
              class="ant-border-top ant-glass-background radius-0 d-flex"
              style="height: 50%"
            >
              <div
                class="ant-border-right pa-2 overflow-y-auto d-flex flex-column"
              >
                <v-subheader>Projects</v-subheader>
                <div class="overflow-y-auto flex-1">
                  <div
                    v-for="project in modelImportModelAccHubProjects"
                    :key="project.id"
                    :class="{ active: isProjectSelected(project) }"
                    class="pa-1 my-1 acc-project-item"
                    @click="fetchAccProjectTopFolders(project)"
                  >
                    {{ project.attributes.name }}
                  </div>
                </div>
              </div>
              <div class="pa-2 flex-1 overflow-y-auto d-flex flex-column">
                <v-subheader>Folders</v-subheader>
                <v-progress-linear
                  :style="{ opacity: accTreeLoading ? 1 : 0 }"
                  class="mb-2"
                  color="primary"
                  indeterminate
                  rounded
                />
                <div class="overflow-y-auto flex-1">
                  <v-treeview
                    :items="accTree"
                    :load-children="fetchFolderContents"
                    :open.sync="folderTreeOpenItems"
                    dense
                    item-children="children"
                    item-key="id"
                    open-on-click
                  >
                    <template #prepend="{ item, open }">
                      <v-icon v-if="item.type === 'folders'">
                        {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                      </v-icon>
                      <v-icon v-else> mdi-cube</v-icon>
                    </template>
                    <template #append="{ item }">
                      <div v-if="item.type === 'items'" class="d-flex">
                        <v-spacer />
                        <v-select
                          v-if="item.displayVersions"
                          v-model="item.selectedVersion"
                          :items="item.versions"
                          dense
                          filled
                          flat
                          hide-details
                          item-text="attributes.versionNumber"
                          item-value="id"
                          outlined
                          placeholder="version"
                          prepend-icon="mdi-note-multiple"
                          return-object
                        />
                        <v-tooltip left>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              :disabled="
                                item.displayVersions
                                  ? item.selectedVersion === undefined
                                  : false
                              "
                              :ripple="false"
                              icon
                              v-bind="attrs"
                              @click="fetchVersionsOrAddToProject(item)"
                              v-on="on"
                            >
                              <v-icon>
                                {{
                                  item.displayVersions
                                    ? 'mdi-plus'
                                    : 'mdi-label-multiple'
                                }}
                              </v-icon>
                            </v-btn>
                          </template>
                          <span>{{
                            item.displayVersions
                              ? 'Add to project'
                              : 'Fetch versions'
                          }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-treeview>
                </div>
              </div>
            </div>
          </div>
        </v-stepper-content>

        <v-stepper-content class="pa-0 full-height" step="2">
          <dhme-import-object-elements
            v-if="
              modelImportSelectedModel &&
              stepper === 2 &&
              statusByUrn(modelImportSelectedModel.urn).status === 'success'
            "
            @importSuccess="stepper = 1"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </div>
  <div v-else class="d-flex justify-center align-center full-height">
    <ant-loading />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AutodeskService from '@/services/forge/autodesk';
import AntLoading from '@/components/AntLoading.vue';
import {
  ACC_ROUTE,
  fetchFolderContents,
  fetchItemVersions,
  fetchTopFolders,
} from '@/services/forge/autodesk-construction-cloud';
import appConfig from '@/appConfig';
import DhmeImportObjectElements from '@/modules/daiwa-house-modular-europe/ModelImport/DhmeImportObjectElements.vue';

export default {
  name: 'DhmeModelImport',
  components: { DhmeImportObjectElements, AntLoading },
  data: () => {
    return {
      modelObjects: [],
      sbsTreeExample: [],
      stepper: 1,
      thumbnailsLoaded: false,
      selectedAccProject: undefined,
      folderTreeOpenItems: [],
      folderContents: [],
      accTree: [],
      accTreeLoading: false,
      statusPollId: null,
    };
  },
  computed: {
    ...mapGetters([
      'project',
      'modelImportModuleInitialized',
      'modelImportModuleModels',
      'modelImportAutodeskAccessToken',
      'modelImportModuleClient',
      'accAccessToken',
      'modelImportModelAccHubProjects',
      'modelImportModelAccProjectTopFolders',
      'modelImportSelectedModel',
    ]),
    isAccConnected() {
      return (
        this.accAccessToken !== undefined && this.modelImportModuleInitialized
      );
    },
    statusByUrn() {
      return (urn) => this.$store.getters.modelImportStatusByUrn(urn);
    },
    propStatusByUrn() {
      return (urn) => this.$store.getters.modelImportPropStatusByUrn(urn);
    },

    combinedByUrn() {
      return (urn) => this.$store.getters.combinedModelStatusByUrn(urn);
    },
  },
  watch: {
    isAccConnected: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchAccHubProjects();
        }
      },
    },
    modelImportSelectedModel: {
      handler(newModel, oldModel) {
        if (newModel && newModel.id) {
          this.$router.replace({
            query: { ...this.$route.query, modelId: newModel.id },
          });
        } else if (!newModel && oldModel) {
          const { modelId, ...restQuery } = this.$route.query;
          this.$router.replace({ query: restQuery }); // Remove `modelId` from query
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    await this.$store.dispatch('loadModelImportData');

    if (!this.$route.query.authenticated) {
      this.loadAutodeskConstructionCloud();
    }

    await this.$store.dispatch('checkModelStatuses');
    await this.$store.dispatch('checkModelPropertiesStatuses');

    // IMPORTANT: await so you get the numeric interval id instead of a Promise
    this.statusPollId = await this.$store.dispatch('startModelStatusPolling', {
      everyMs: 10000,
    });

    const modelIdFromQuery = this.$route.query.modelId;
    if (modelIdFromQuery) {
      const model = this.modelImportModuleModels.find(
        (m) => m.id === modelIdFromQuery
      );
      if (model) {
        const d = this.statusByUrn(model.urn);
        const p = this.propStatusByUrn(model.urn);
        if (d.status === 'success' && p.status === 'success') {
          this.$store.commit('module_model_import_set_model', model);
          this.stepper = 2;
        } else {
          this.$store.commit('showNotification', {
            color: 'warning',
            content:
              d.status !== 'success'
                ? 'Model is still processing.'
                : 'Model is ready, but properties are still indexing.',
          });
        }
      }
    }

    // thumbnails only for derivative-ready models
    for (const model of this.modelImportModuleModels) {
      const d = this.statusByUrn(model.urn);
      if (d.status === 'success') {
        model.thumbnail = await this.fetchModelThumbnail(model);
      }
    }

    this.thumbnailsLoaded = true;
  },
  beforeDestroy() {
    if (this.statusPollId && typeof this.statusPollId === 'number') {
      clearInterval(this.statusPollId);
    }
  },
  methods: {
    isProjectSelected(project) {
      if (this.selectedAccProject) {
        return this.selectedAccProject.id === project.id;
      }
      return false;
    },
    fetchAccHubProjects() {
      this.$store.dispatch('fetchAccHubProjects');
    },
    async fetchAccProjectTopFolders(project) {
      this.accTreeLoading = true;
      this.accTree = [];
      this.selectedAccProject = project;
      const topFolders = await fetchTopFolders(
        this.accAccessToken,
        this.modelImportModuleClient.acc_hub,
        project.id
      );
      topFolders.forEach((folder) => {
        this.accTree.push({
          id: folder.id,
          type: folder.type,
          name: folder.attributes.name,
          children: [],
        });
      });
      this.accTreeLoading = false;
    },
    fetchFolderContents(item) {
      if (item.type === 'folders') {
        this.accTreeLoading = true;
        fetchFolderContents(
          this.accAccessToken,
          this.selectedAccProject.id,
          item.id
        ).then((contents) => {
          let treeContent = [];
          contents.forEach((contentItem) => {
            let data = {
              id: contentItem.id,
              name:
                contentItem.attributes.name !== undefined
                  ? contentItem.attributes.name
                  : contentItem.attributes.displayName,
              type: contentItem.type,
            };
            if (data.type === 'folders') {
              data.children = [];
            }
            if (data.type === 'items') {
              data.displayVersions = false;
            }
            treeContent.push(data);
          });
          item.children.push(...treeContent);
          this.folderTreeOpenItems.push(item.id);
          this.accTreeLoading = false;
          return contents;
        });
      }
    },
    async fetchVersionsOrAddToProject(item) {
      if (!item.displayVersions) {
        // fetch versions
        item.versions = await fetchItemVersions(
          this.accAccessToken,
          this.selectedAccProject.id,
          item.id
        );
        item.displayVersions = true;
      } else {
        await this.$store.dispatch('addModelToProject', {
          urn: item.selectedVersion.relationships.derivatives.data.id,
          name: item.selectedVersion.attributes.name,
          version: item.selectedVersion.attributes.versionNumber,
          from_acc: true,
        });
        await this.$store.dispatch('checkModelStatuses');
        await this.$store.dispatch('checkModelPropertiesStatuses');
      }
    },
    async forgeAuthenticate() {
      let credentials = this.modelImportModuleClient;
      await this.$store.dispatch('saveCredentials', credentials);
      const token = await AutodeskService.fetchToken(
        credentials.client_id,
        credentials.secret,
        credentials.server_region
      );
      this.$store.commit('set_autodesk_authentication_token', token);
    },
    async fetchModelThumbnail(model) {
      let credentials = this.modelImportModuleClient;
      let server = credentials.server_region === 'EMEA' ? 'regions/eu/' : '';

      // fetch thumbnail
      let url = `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/${model.urn}/thumbnail`;
      const headers = new Headers();
      headers.set(
        'Authorization',
        `Bearer ${
          model.from_acc
            ? this.accAccessToken
            : this.modelImportAutodeskAccessToken
        }`
      );
      const imageResponse = await fetch(url, { headers });
      // parse thumbnail to readable data
      const blob = await imageResponse.blob();
      return URL.createObjectURL(blob);
    },
    async selectModel(model) {
      const combined = this.combinedByUrn(model.urn);

      if (combined.label !== 'Ready') {
        this.$store.commit('showNotification', {
          color: combined.color === 'error' ? 'error' : 'warning',
          content:
            combined.label === 'Processing…' ||
            combined.label.startsWith('Processing')
              ? 'Model is still processing.'
              : combined.label === 'Indexing props…'
                ? 'Properties are still being indexed. Try again shortly.'
                : combined.label === 'Props failed'
                  ? 'Properties indexing failed for this model.'
                  : 'Model is not ready yet.',
        });
        return;
      }

      this.$store.commit('module_model_import_set_model', model);
      this.stepper = 2;
    },

    loadAutodeskConstructionCloud() {
      this.$cookies.set(ACC_ROUTE, `${this.$route.path}`);
      window.open(
        `https://developer.api.autodesk.com/authentication/v2/authorize?response_type=code&client_id=${
          appConfig.VUE_APP_ACC_CLIENT_ID
        }&redirect_uri=${encodeURI(
          appConfig.VUE_APP_ACC_CLIENT_CALLBACK_URL
        )}&scope=data:read`,
        '_self'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.model-tiles-container {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-auto-rows: minmax(120px, auto);
  grid-gap: 1rem;
  overflow: auto;

  .model-tile {
    transition: 200ms;
    cursor: pointer;
    border-left: solid 4px var(--v-primary-base);

    &:hover {
      background-color: white;
      box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;

      .model-tile-add-icon {
        color: var(--v-primary-base);
      }
    }
  }
}

.acc-project-item {
  transition: 200ms;
  cursor: pointer;

  &:hover {
    border-left: solid 4px var(--v-primary-base);
    background-color: white;
  }

  &.active {
    border-left: solid 4px var(--v-primary-base);
    background-color: white;
  }
}
</style>
<style>
.v-stepper__wrapper {
  height: 100%;
}
</style>
